import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};
import { db, auth } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaLock, FaCheckCircle, FaFilePdf, FaArrowLeft } from 'react-icons/fa';

export default function PaymentPage() {
  const [loading, setLoading] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState('');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if Razorpay is loaded
  useEffect(() => {
    // Check if Razorpay is already loaded
    if (window.Razorpay) {
      setRazorpayLoaded(true);
      setLoading(false);
      return;
    }

    // Fallback: Try to load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      console.log('Razorpay script loaded successfully');
      setRazorpayLoaded(true);
      setLoading(false);
    };
    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      setError('Failed to load payment processor. Please refresh the page and try again.');
      setLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup
      document.body.removeChild(script);
    };
  }, []);

  // Get assessment ID from URL or location state
  const searchParams = new URLSearchParams(location.search);
  const assessmentId = searchParams.get('assessmentId') || (location.state?.assessmentId || '');
  const redirectTo = location.state?.redirectTo || '/app/report';

  useEffect(() => {
    // Load Razorpay script
    const loadRazorpay = async () => {
      try {
        await loadScript('https://checkout.razorpay.com/v1/checkout.js');
        setLoading(false);
      } catch (err) {
        console.error('Failed to load Razorpay', err);
        setError('Failed to load payment processor. Please try again later.');
        setLoading(false);
      }
    };

    loadRazorpay();
  }, []);

  const handlePayment = async () => {
    if (!auth.currentUser) {
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      setError('Payment processor is not ready. Please wait a moment and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get the ID token for authentication
      const idToken = await auth.currentUser.getIdToken();
      
      // Define the amount in INR (will be converted to paise in the backend)
      const amountInInr = 99; // Offer price: ₹99
      
      // Use the Cloud Run URL
      const baseUrl = 'https://api-d7lnrhlflq-uc.a.run.app';
      
      // For local development with emulator
      // const baseUrl = 'http://localhost:5001/asdflobeta/us-central1/api';
      
      // Call your backend to create an order
      const response = await fetch(`${baseUrl}/api/createRazorpayOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          amount: amountInInr, // Send amount in INR (will be converted to paise in backend)
          currency: 'INR',
          receipt: `assessment_${assessmentId}`,
          notes: {
            amount: amountInInr, // Include amount in notes for verification
            assessmentId,
            type: 'assessment_report',
            userId: auth.currentUser.uid,
            currency: 'INR'
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create payment order');
      }

      const { order, firestoreOrderId } = await response.json();
      
      // Ensure amount is in paise and is a number
      const amountInPaise = typeof order.amount === 'number' ? order.amount : parseInt(order.amount, 10);
      
      // Store the Firestore order ID for verification
      const orderId = firestoreOrderId || order.id;
      
      // First, get the Razorpay key from the backend
      const configResponse = await fetch(`${baseUrl}/api/getRazorpayKey`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!configResponse.ok) {
        throw new Error('Failed to get payment configuration');
      }

      const { key: razorpayKey } = await configResponse.json();

      const options = {
        key: razorpayKey,
        amount: amountInPaise, // Should already be in paise from the order
        currency: (order.currency || 'INR').toUpperCase(),
        name: 'ASD Flo',
        description: 'M-CHAT Assessment Report',
        order_id: order.id,
        
        // Payment success handler
        handler: async function(response) {
          try {
            // Verify payment on your backend
            const verifyResponse = await fetch(`${baseUrl}/api/verifyPayment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: orderId, // Use the Firestore order ID we stored
                notes: {
                  amount: amountInInr,
                  assessmentId,
                  type: 'assessment_report',
                  userId: auth.currentUser.uid,
                  currency: 'INR'
                }
              })
            });

            if (!verifyResponse.ok) {
              const errorData = await verifyResponse.json().catch(() => ({}));
              console.error('Payment verification failed:', errorData);
              throw new Error(errorData.error || 'Payment verification failed');
            }

            // Update Firestore with payment details
            const assessmentRef = doc(db, 'assessments', assessmentId);
            await updateDoc(assessmentRef, {
              payment: {
                status: 'completed',
                amount: amountInInr,
                currency: 'INR',
                transactionId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id || order.id,
                timestamp: serverTimestamp()
              },
              status: 'completed',
              updatedAt: serverTimestamp()
            });

            setPaymentSuccess(true);
            setOrderDetails({
              orderId: response.razorpay_order_id || order.id,
              paymentId: response.razorpay_payment_id,
              amount: amountInInr
            });
          } catch (err) {
            console.error('Payment verification error:', err);
            setError('Payment verification failed. Please contact support.');
          }
        },
        
        // Pre-fill user details if available
        prefill: {
          name: auth.currentUser.displayName || '',
          email: auth.currentUser.email || '',
          contact: auth.currentUser.phoneNumber || ''
        },
        
        // UI/UX settings
        theme: {
          color: '#4F46E5' // Indigo-600
        },
        
        // Modal settings
        modal: {
          ondismiss: function() {
            setError('Payment was cancelled');
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setLoading(false);
    } catch (err) {
      console.error('Payment error:', err);
      setError('Failed to process payment. Please try again.');
      setLoading(false);
    }
  };

  if (loading && !paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Preparing your payment...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-12 max-w-4xl">
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="text-4xl text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Unlock Your Child's Full Assessment Report</h1>
            <p className="text-lg text-indigo-600 font-semibold mb-4">Limited Time Offer!</p>
            <p className="text-gray-600 mb-8">Thank you for your purchase. Your assessment report is now available.</p>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-8 text-left max-w-md mx-auto">
              <h3 className="font-medium text-gray-900 mb-4">Order Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-medium">{orderDetails.orderId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment ID:</span>
                  <span className="font-medium">{orderDetails.paymentId}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold pt-3 border-t border-gray-200 mt-3">
                  <span>Amount Paid:</span>
                  <span>₹{orderDetails.amount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate(`/app/report/${assessmentId}`)}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <FaFilePdf /> View Report
              </button>
              <button
                onClick={() => navigate('/app/report')}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back to Assessments
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Complete Your Purchase</h1>
                <p className="text-lg text-indigo-200 font-semibold">Limited Time Offer!</p>
                <p className="text-blue-100 mt-1">M-CHAT Assessment Report</p>
              </div>
              <div className="text-right">
                  <span className="text-4xl font-extrabold text-white">₹99</span>
                  <span className="text-lg text-blue-200 line-through ml-2">₹999</span>
                  <p className="text-xs text-blue-100 mt-1">One-time payment</p>
                </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="p-6 md:p-8">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                <p>{error}</p>
              </div>
            )}

            <div className="space-y-6">
              {/* Order Summary */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Order Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">M-CHAT Assessment Report</span>
                    <span className="font-medium">₹99.00</span>
                  </div>
                  <div className="border-t border-gray-200 my-2"></div>
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>₹99.00</span>
                  </div>
                </div>
              </div>

              {/* Secure Payment Info */}
              <div className="bg-blue-50 p-4 rounded-lg flex items-start">
                <FaLock className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-800">Secure Payment</h4>
                  <p className="text-sm text-blue-700">Your payment information is encrypted and secure. We do not store your credit card details.</p>
                </div>
              </div>

              {/* Payment Button */}
              <button
                onClick={handlePayment}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-3 px-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaLock className="text-white" />
                    Pay Securely - ₹99.00
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => navigate(-1)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center"
                >
                  <FaArrowLeft className="mr-1" /> Back to previous page
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
